// The shell: rail + module menu + top bar + content + docked side panel +
// phone bottom bar + idle lock. Structure follows useLayout(); the visual
// sizes follow CSS media/container queries (shell.css).

import { Suspense, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { Outlet, useLocation, useMatches } from 'react-router-dom';
import { CircleAlert, RefreshCw } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { KpiStrip } from '@/components/KpiStrip';
import { DataTable } from '@/components/DataTable';
import { installShortcuts, registerShortcut } from '@/lib/shortcuts';
import { sessionStore } from '@/lib/auth';
import { idleLockMs, startIdleWatch } from '@/lib/idle-lock';
import { startAutoReplay } from '@/lib/offline-queue';
import { useLayout } from '@/lib/layout';
import { DARK_MODE_FLAG, useTheme } from '@/lib/theme';
import { BottomBar } from './BottomBar';
import { useErp } from './erp-context';
import { Forbidden, LandingContext, landingFor } from './Forbidden';
import { activeGroupFor, buildRail, denial, moduleGate, type GateContext } from './gates';
import { LockScreen } from './LockScreen';
import type { ErpModule, ErpRouteHandle } from './module-types';
import { ModuleMenu } from './ModuleMenu';
import { ModuleRail } from './ModuleRail';
import { MODULES } from './modules';
import { PANEL_DOCK_ID, usePanelOpen } from './SidePanel';
import { TopBar } from './TopBar';
import { useToast } from './Toast';
import { LockedPeriodHost } from '@/features/accounts/close/LockedPeriodDialog';
import { BookingPanelHost, openBookingPanel } from '@/features/booking/public';
import { ReservationPanelHost } from '@/features/hotel/public';

/** First refusal among the matched routes' gates (module gate, then route gates). */
export function routeDenial(matches: Array<{ handle: unknown }>, ctx: GateContext) {
  for (const m of matches) {
    const h = m.handle as ErpRouteHandle | undefined;
    const d = denial(h?.gate, ctx);
    if (d) return d;
  }
  return null;
}

/** The content outlet with route gates applied (deep link → <Forbidden/>, landing from LandingContext). */
export function ContentGate() {
  const { gateContext } = useErp();
  const matches = useMatches();
  const d = routeDenial(matches, gateContext);
  if (d) return <Forbidden reason={d.reason} minRole={d.minRole} />;
  return (
    <Suspense fallback={<ContentSkeleton />}>
      <Outlet />
    </Suspense>
  );
}

/** Lazy-route fallback in the shape of a typical page (head + KPI shells + table). */
export function ContentSkeleton() {
  return (
    <div aria-busy="true" data-testid="content-skeleton" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <KpiStrip cells={[]} loading />
      <DataTable aria-label="" columns={[{ key: 'a', label: '' }, { key: 'b', label: '' }, { key: 'c', label: '', align: 'end', width: 120 }]} rows={undefined} rowKey={() => ''} loading />
    </div>
  );
}

/**
 * GET /v1/business/features failed (online): every erp.* module is absent
 * (fail closed) — say so inline, in the OfflineStrip's shape, with a retry.
 */
export function FeaturesNotice() {
  const { featuresFailed, retryFeatures } = useErp();
  if (!featuresFailed) return null;
  return (
    <div className="e-offline" role="status" data-testid="features-error">
      <CircleAlert aria-hidden="true" />
      <span>تعذّر تحميل ميزات النشاط. أعد المحاولة.</span>
      <Button size="sm" variant="ghost" icon={RefreshCw} onClick={retryFeatures}>
        إعادة المحاولة
      </Button>
    </div>
  );
}

export function AppShell({ modules = MODULES }: { modules?: readonly ErpModule[] }) {
  const erp = useErp();
  const layout = useLayout();
  const panelOpen = usePanelOpen();
  const { pathname, search } = useLocation();
  const { toast } = useToast();
  const searchRef = useRef<HTMLInputElement>(null);

  const rail = useMemo(() => buildRail([...modules], erp.gateContext), [modules, erp.gateContext]);
  const matches = useMatches();
  const routeModule = [...matches].reverse().map((m) => (m.handle as ErpRouteHandle | undefined)?.moduleKey).find(Boolean);
  const routeGroup = modules.find((m) => m.key === routeModule)?.rail ?? null;
  const activeGroup = activeGroupFor(pathname, rail, routeGroup, search);
  const activeEntry = rail.find((e) => e.group === activeGroup) ?? null;
  const landing = useMemo(() => landingFor(rail, erp.t), [rail, erp.t]);
  const idleSeconds = useSyncExternalStore(sessionStore.subscribe, sessionStore.getIdleLockSeconds, sessionStore.getIdleLockSeconds);

  // Theme (board 34): the stored preference stamps <html data-theme> only while
  // ui.dark-mode is on; off ⇒ light whatever is stored (lib/theme.ts).
  useTheme(erp.flag(DARK_MODE_FLAG));

  // Idle lock: the server's idle_lock_seconds (node) or 15 minutes (cloud).
  useEffect(() => {
    const idle = startIdleWatch({ timeoutMs: idleLockMs(idleSeconds) });
    return () => idle.stop();
  }, [idleSeconds]);

  // Keyboard shortcuts, local-queue auto replay.
  useEffect(() => installShortcuts(), []);

  // After a replay the server holds new documents (or refused some): every
  // screen reads them again — a statement fetched on reconnect, BEFORE the
  // replay finished, must not stay empty (e2e invoice-receipt-statement-2).
  const qc = useQueryClient();
  useEffect(() => {
    const stop = startAutoReplay(undefined, (s) => {
      for (const [temp, final] of Object.entries(s.renumbered)) toast(`تمت المزامنة: ${temp} ← ${final}`);
      if (s.synced > 0 || s.conflicts > 0) void qc.invalidateQueries();
    });
    return stop;
  }, [toast, qc]);

  const bookingVisible = rail.some((e) => e.moduleKeys.includes('booking')) || modules.some((m) => m.key === 'booking' && !denial(moduleGate(m), erp.gateContext));

  // Default shortcut handlers (screens may stack their own on top). N opens
  // «موعد جديد» OVER the current page (board: «اليوم» stays behind).
  useEffect(() => {
    const offs = [
      registerShortcut('search', () => searchRef.current?.focus()),
      registerShortcut('print', () => window.print()),
    ];
    if (bookingVisible) offs.push(registerShortcut('new-appointment', () => openBookingPanel()));
    return () => offs.forEach((off) => off());
  }, [bookingVisible]);

  return (
    <div className="e-shell" data-layout={layout} data-panel-open={panelOpen ? 'true' : 'false'} data-testid="app-shell">
      {layout !== 'phone' && <ModuleRail rail={rail} active={activeGroup} layout={layout} />}
      {layout === 'wide' && <ModuleMenu entry={activeEntry} />}
      <div className="e-main">
        <TopBar ref={searchRef} />
        {layout === 'phone' && <ModuleMenu entry={activeEntry} variant="strip" />}
        <main className="e-content cq" id="main" data-testid="content">
          <FeaturesNotice />
          <LandingContext.Provider value={landing}>
            <ContentGate />
          </LandingContext.Provider>
        </main>
      </div>
      <div className="e-panel-dock" id={PANEL_DOCK_ID} />
      {layout === 'phone' && <BottomBar rail={rail} active={activeGroup} />}
      {/* «موعد جديد» from the top bar / N, over whatever page is open (the /booking/new deep link renders its own). */}
      {bookingVisible && <BookingPanelHost />}
      {/* «حجز جديد» from the hotel top bar, over whatever hotel page is open (the /hotel/reservations/new deep link renders its own). */}
      {erp.termProfile === 'hotel' && rail.some((e) => e.moduleKeys.includes('hotelReservations')) && <ReservationPanelHost />}
      <LockScreen />
      {/* The one global closed-period dialog (BRIEF rule 8); fed by the MutationCache + showLockedPeriod. */}
      <LockedPeriodHost />
    </div>
  );
}
